import { WifiOff } from 'lucide-react';
import { formatSilence } from '../../lib/banepadHeartbeat';

export interface BanepadOfflineBannerProps {
  /** Epoch ms when the outage started. */
  since: number;
  /** The pad's clock, passed in so this does not run a timer of its own. */
  now: number;
  /** True while a result is waiting to reach the server. */
  sending?: boolean;
}

/**
 * «No connection» AT THE POINT OF INTERACTION, without taking the screen (#373).
 *
 * ── WHY THIS EXISTS BESIDE THE FULL-SCREEN VERSION ──────────────────
 *
 * `BanepadDeviceOffline` covers the pad. That is right when the court has
 * nothing on it: there is nothing to do, and a wall-mounted tablet showing a
 * support number is more use than a dead idle screen.
 *
 * It is wrong the moment a match is in play. From the hall (#216): «I cannot
 * enter anything while offline. It just show a offline screen.» The match was
 * on the pad, the retry queue behind the submit button was working perfectly
 * — it carried a result across an outage, eleven attempts, applied exactly
 * once — and the operator could not reach it, because a `fixed inset-0`
 * overlay had covered the buttons.
 *
 * So the state is reported and the pad stays usable. Losing Wi-Fi for two
 * minutes must not cost a court its match.
 *
 * ── WHAT IT DOES NOT SAY ────────────────────────────────────────────
 *
 * Not «saved», not «registered», not anything about the result having landed.
 * Nothing is authoritative until the server says so, and a kiosk that claims
 * otherwise is how a match gets entered twice. It says the score is HELD and
 * will be sent — which is exactly what the retry queue does.
 */
export function BanepadOfflineBanner({ since, now, sending = false }: BanepadOfflineBannerProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed left-1/2 -translate-x-1/2 flex items-center gap-3 rounded-full px-5 py-2 font-semibold"
      style={{
        // TOP, because the result banner already owns the bottom — and
        // neither may sit over the controls between them.
        top: '2.5vh',
        zIndex: 60,
        fontSize: 'min(3.2vw, 20px)',
        background: '#78350f',
        color: '#fef3c7',
        border: '1px solid #f59e0b',
      }}
    >
      <WifiOff size={20} aria-hidden="true" />
      <span>
        {sending
          ? 'Ingen forbindelse — resultatet sendes når nettet er tilbake'
          : 'Ingen forbindelse — du kan fortsette, pad-en sender når nettet er tilbake'}
      </span>
      <span style={{ opacity: 0.75, fontWeight: 400 }}>{formatSilence(now - since)}</span>
    </div>
  );
}
